import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI, stylistAPI } from '../../services/api';
import ClientLayout from '../../components/ClientLayout';
import { 
  MessageSquare, 
  Plus, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Sparkles, 
  ShoppingBag,
  Upload,
  X,
  Loader2,
  ArrowLeft,
  ChevronRight
} from 'lucide-react';

const statusConfig = {
  new: { label: 'Новый', color: '#f59e0b', icon: AlertCircle },
  in_progress: { label: 'В работе', color: '#3b82f6', icon: Clock },
  completed: { label: 'Выполнен', color: '#22c55e', icon: CheckCircle }
};

const typeConfig = {
  look: { label: 'Образ', icon: Sparkles },
  shopping: { label: 'Покупка', icon: ShoppingBag }
};

const ClientRequests = () => {
  const navigate = useNavigate();
  const clientId = localStorage.getItem('clientId');
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    request_type: 'look',
    description: '',
    budget: '',
    deadline: ''
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [noBalanceWarning, setNoBalanceWarning] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchRequests();
    }
  }, [clientId]);

  const fetchRequests = async () => {
    try {
      const data = await clientAPI.getRequests(clientId);
      setRequests(data);
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      const uploadPromises = files.map(file => stylistAPI.uploadImage(file));
      const results = await Promise.all(uploadPromises);
      const newUrls = results.map(r => r.url);
      setPhotos([...photos, ...newUrls]);
    } catch (err) {
      setError('Ошибка загрузки фото');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      setError('Введите описание запроса');
      return;
    }

    setSaving(true);
    setError('');
    setNoBalanceWarning(false);

    try {
      const requestData = {
        request_type: formData.request_type,
        description: formData.description.trim(),
        photos: photos,
        budget: formData.budget.trim() || null,
        deadline: formData.deadline || null
      };

      const result = await clientAPI.createRequest(clientId, requestData);
      setRequests([result, ...requests]);
      
      // Check if no balance warning
      if (result.no_balance_warning) {
        setNoBalanceWarning(true);
      } else {
        setShowCreateForm(false);
        setFormData({ request_type: 'look', description: '', budget: '', deadline: '' });
        setPhotos([]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания запроса');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const fetchRequestDetail = async (request) => {
    try {
      const detail = await clientAPI.getRequestDetail(clientId, request.id);
      setSelectedRequest(detail);
    } catch (err) {
      console.error('Error fetching request detail:', err);
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <Loader2 size={32} style={{ color: 'var(--accent-gold)', animation: 'spin 1s linear infinite' }} />
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </ClientLayout>
    );
  }

  // Request Detail View
  if (selectedRequest) {
    const status = statusConfig[selectedRequest.status] || statusConfig.new;
    const type = typeConfig[selectedRequest.request_type] || typeConfig.look;
    const StatusIcon = status.icon;
    const TypeIcon = type.icon;

    return (
      <ClientLayout>
        <div style={{ maxWidth: '800px', margin: '0 auto' }} data-testid="request-detail-view">
          <button
            onClick={() => setSelectedRequest(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              background: 'none',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              color: 'var(--mid-gray)',
              cursor: 'pointer',
              marginBottom: 'var(--space-l)',
              padding: 0
            }}
          >
            <ArrowLeft size={16} />
            Все запросы
          </button>

          <div style={{
            backgroundColor: 'var(--white)',
            padding: 'var(--space-xl)',
            border: '1px solid var(--soft-gray)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-m)',
              marginBottom: 'var(--space-l)'
            }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                backgroundColor: 'var(--cream)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem'
              }}>
                <TypeIcon size={14} />
                {type.label}
              </span>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                backgroundColor: `${status.color}15`,
                color: status.color,
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem'
              }}>
                <StatusIcon size={14} />
                {status.label}
              </span>
            </div>

            {/* Description */}
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '1rem',
              lineHeight: 1.6,
              marginBottom: 'var(--space-l)',
              whiteSpace: 'pre-wrap'
            }}>
              {selectedRequest.description}
            </p>

            {/* Budget & Deadline */}
            {(selectedRequest.budget || selectedRequest.deadline) && (
              <div style={{
                display: 'flex',
                gap: 'var(--space-xxl)',
                marginBottom: 'var(--space-l)',
                padding: 'var(--space-m)',
                backgroundColor: 'var(--cream)'
              }}>
                {selectedRequest.budget && (
                  <div>
                    <span style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem',
                      color: 'var(--light-gray)',
                      textTransform: 'uppercase'
                    }}>
                      Бюджет
                    </span>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.9375rem',
                      marginTop: '4px'
                    }}>
                      {selectedRequest.budget}
                    </p>
                  </div>
                )}
                {selectedRequest.deadline && (
                  <div>
                    <span style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem',
                      color: 'var(--light-gray)',
                      textTransform: 'uppercase'
                    }}>
                      Дедлайн
                    </span>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.9375rem',
                      marginTop: '4px'
                    }}>
                      {new Date(selectedRequest.deadline).toLocaleDateString('ru-RU')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Photos */}
            {selectedRequest.photos && selectedRequest.photos.length > 0 && (
              <div style={{ marginBottom: 'var(--space-l)' }}>
                <h4 style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'var(--mid-gray)',
                  marginBottom: 'var(--space-s)'
                }}>
                  Приложенные фото
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 'var(--space-s)'
                }}>
                  {selectedRequest.photos.map((photo, idx) => (
                    <a
                      key={idx}
                      href={photo}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={photo}
                        alt=""
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          objectFit: 'cover'
                        }}
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Linked Results */}
            {(selectedRequest.linked_looks?.length > 0 || selectedRequest.linked_recommendations?.length > 0) && (
              <div style={{
                borderTop: '1px solid var(--soft-gray)',
                paddingTop: 'var(--space-l)'
              }}>
                <h4 style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'var(--mid-gray)',
                  marginBottom: 'var(--space-m)'
                }}>
                  Результаты по запросу
                </h4>

                {selectedRequest.linked_looks?.length > 0 && (
                  <div style={{ marginBottom: 'var(--space-m)' }}>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.8125rem',
                      marginBottom: 'var(--space-s)'
                    }}>
                      Подобранные образы:
                    </p>
                    <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                      {selectedRequest.linked_looks.map((look) => (
                        <div
                          key={look.id}
                          onClick={() => navigate('/client/looks')}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-s)',
                            padding: '8px 12px',
                            backgroundColor: 'var(--cream)',
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.8125rem',
                            cursor: 'pointer'
                          }}
                        >
                          {look.look_images?.[0] && (
                            <img
                              src={look.look_images[0]}
                              alt=""
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                            />
                          )}
                          {look.name}
                          <ChevronRight size={14} style={{ color: 'var(--light-gray)' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedRequest.linked_recommendations?.length > 0 && (
                  <div>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.8125rem',
                      marginBottom: 'var(--space-s)'
                    }}>
                      Рекомендации к покупке:
                    </p>
                    <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                      {selectedRequest.linked_recommendations.map((rec) => (
                        <div
                          key={rec.id}
                          onClick={() => navigate('/client/recommendations')}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-s)',
                            padding: '8px 12px',
                            backgroundColor: 'var(--cream)',
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.8125rem',
                            cursor: 'pointer'
                          }}
                        >
                          {rec.image_url && (
                            <img
                              src={rec.image_url}
                              alt=""
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                            />
                          )}
                          {rec.description.substring(0, 25)}...
                          <ChevronRight size={14} style={{ color: 'var(--light-gray)' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              color: 'var(--light-gray)',
              marginTop: 'var(--space-l)'
            }}>
              Создан {new Date(selectedRequest.created_at).toLocaleString('ru-RU')}
            </p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  // Create Form View
  if (showCreateForm) {
    return (
      <ClientLayout>
        <div style={{ maxWidth: '600px', margin: '0 auto' }} data-testid="create-request-form">
          <button
            onClick={() => setShowCreateForm(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              background: 'none',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              color: 'var(--mid-gray)',
              cursor: 'pointer',
              marginBottom: 'var(--space-l)',
              padding: 0
            }}
          >
            <ArrowLeft size={16} />
            Назад
          </button>

          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            fontWeight: 300,
            marginBottom: 'var(--space-xl)'
          }}>
            Новый запрос
          </h1>

          <form onSubmit={handleSubmit}>
            {/* Request Type */}
            <div style={{ marginBottom: 'var(--space-l)' }}>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 'var(--space-s)',
                color: 'var(--mid-gray)'
              }}>
                Что вам нужно? *
              </label>
              <div style={{ display: 'flex', gap: 'var(--space-m)' }}>
                <label style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-s)',
                  padding: 'var(--space-m)',
                  border: formData.request_type === 'look' 
                    ? '2px solid var(--black)' 
                    : '1px solid var(--soft-gray)',
                  backgroundColor: formData.request_type === 'look' 
                    ? 'var(--cream)' 
                    : 'var(--white)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem'
                }}>
                  <input
                    type="radio"
                    name="request_type"
                    value="look"
                    checked={formData.request_type === 'look'}
                    onChange={(e) => setFormData({ ...formData, request_type: e.target.value })}
                    style={{ display: 'none' }}
                  />
                  <Sparkles size={18} />
                  Подобрать образ
                </label>
                <label style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-s)',
                  padding: 'var(--space-m)',
                  border: formData.request_type === 'shopping' 
                    ? '2px solid var(--black)' 
                    : '1px solid var(--soft-gray)',
                  backgroundColor: formData.request_type === 'shopping' 
                    ? 'var(--cream)' 
                    : 'var(--white)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem'
                }}>
                  <input
                    type="radio"
                    name="request_type"
                    value="shopping"
                    checked={formData.request_type === 'shopping'}
                    onChange={(e) => setFormData({ ...formData, request_type: e.target.value })}
                    style={{ display: 'none' }}
                  />
                  <ShoppingBag size={18} />
                  Помочь с покупкой
                </label>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: 'var(--space-l)' }}>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 'var(--space-s)',
                color: 'var(--mid-gray)'
              }}>
                Опишите ваш запрос *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={formData.request_type === 'look' 
                  ? "Например: Нужен образ для делового ужина в ресторане. Дресс-код smart casual..."
                  : "Например: Ищу классическое пальто на осень, нейтрального цвета..."}
                rows={5}
                style={{
                  width: '100%',
                  padding: 'var(--space-m)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  border: '1px solid var(--soft-gray)',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Photos */}
            <div style={{ marginBottom: 'var(--space-l)' }}>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 'var(--space-s)',
                color: 'var(--mid-gray)'
              }}>
                Референсы (необязательно)
              </label>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem',
                color: 'var(--light-gray)',
                marginBottom: 'var(--space-m)'
              }}>
                Добавьте фото, которые помогут стилисту лучше понять ваш запрос
              </p>
              
              {photos.length > 0 && (
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-s)',
                  flexWrap: 'wrap',
                  marginBottom: 'var(--space-m)'
                }}>
                  {photos.map((photo, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img
                        src={photo}
                        alt=""
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--black)',
                          color: 'var(--white)',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <label style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-s)',
                padding: '10px 16px',
                backgroundColor: 'var(--cream)',
                border: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem',
                cursor: uploading ? 'wait' : 'pointer'
              }}>
                {uploading ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Upload size={16} />
                )}
                {uploading ? 'Загрузка...' : 'Добавить фото'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
              </label>
            </div>

            {/* Budget (only for shopping) & Deadline */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: formData.request_type === 'shopping' ? '1fr 1fr' : '1fr',
              gap: 'var(--space-m)',
              marginBottom: 'var(--space-xl)'
            }}>
              {formData.request_type === 'shopping' && (
                <div>
                  <label style={{
                    display: 'block',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 'var(--space-s)',
                    color: 'var(--mid-gray)'
                  }}>
                    Бюджет
                  </label>
                  <input
                    type="text"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="Например: до 30 000 ₽"
                    style={{
                      width: '100%',
                      padding: 'var(--space-m)',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.875rem',
                      border: '1px solid var(--soft-gray)'
                    }}
                  />
                </div>
              )}
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 'var(--space-s)',
                  color: 'var(--mid-gray)'
                }}>
                  Когда нужно
                </label>
                <input
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--space-m)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    border: '1px solid var(--soft-gray)'
                  }}
                />
              </div>
            </div>

            {error && (
              <p style={{
                color: '#ef4444',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                marginBottom: 'var(--space-m)'
              }}>
                {error}
              </p>
            )}

            {noBalanceWarning && (
              <div style={{
                padding: 'var(--space-l)',
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                marginBottom: 'var(--space-l)'
              }}>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.9375rem',
                  color: '#92400e',
                  marginBottom: 'var(--space-s)'
                }}>
                  ⚠️ Ваш запрос отправлен, но у вас закончились действия в подписке.
                </p>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: '#92400e'
                }}>
                  Стилист уведомлён и свяжется с вами для пополнения подписки.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setNoBalanceWarning(false);
                    setShowCreateForm(false);
                    setFormData({ request_type: 'look', description: '', budget: '', deadline: '' });
                    setPhotos([]);
                  }}
                  style={{
                    marginTop: 'var(--space-m)',
                    padding: '10px 20px',
                    backgroundColor: '#92400e',
                    color: 'white',
                    border: 'none',
                    fontFamily: 'var(--font-body)',
                    cursor: 'pointer'
                  }}
                >
                  Понятно
                </button>
              </div>
            )}

            {!noBalanceWarning && (
              <button
                type="submit"
                disabled={saving || !formData.description.trim()}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: 'var(--black)',
                  color: 'var(--white)',
                  border: 'none',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.9375rem',
                  cursor: saving ? 'wait' : 'pointer',
                  opacity: saving || !formData.description.trim() ? 0.5 : 1
                }}
              >
                {saving ? 'Отправка...' : 'Отправить запрос'}
              </button>
            )}
          </form>

          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </ClientLayout>
    );
  }

  // Requests List View
  return (
    <ClientLayout>
      <div style={{ maxWidth: '800px', margin: '0 auto' }} data-testid="client-requests-page">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-xl)'
        }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            fontWeight: 300
          }}>
            Мои запросы
          </h1>
          <button
            onClick={() => setShowCreateForm(true)}
            data-testid="new-request-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              padding: '12px 20px',
              backgroundColor: 'var(--black)',
              color: 'var(--white)',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            <Plus size={18} />
            Новый запрос
          </button>
        </div>

        {requests.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: 'var(--space-xxl)',
            backgroundColor: 'var(--white)',
            border: '1px solid var(--soft-gray)'
          }}>
            <MessageSquare size={48} style={{ color: 'var(--soft-gray)', marginBottom: 'var(--space-m)' }} />
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.25rem',
              fontWeight: 400,
              marginBottom: 'var(--space-s)'
            }}>
              У вас пока нет запросов
            </h3>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-l)'
            }}>
              Создайте запрос на подбор образа или помощь с покупкой
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-s)',
                padding: '14px 28px',
                backgroundColor: 'var(--black)',
                color: 'var(--white)',
                border: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: '0.9375rem',
                cursor: 'pointer'
              }}
            >
              <Plus size={18} />
              Создать запрос
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-m)' }}>
            {requests.map((request) => {
              const status = statusConfig[request.status] || statusConfig.new;
              const type = typeConfig[request.request_type] || typeConfig.look;
              const StatusIcon = status.icon;
              const TypeIcon = type.icon;

              return (
                <div
                  key={request.id}
                  onClick={() => fetchRequestDetail(request)}
                  data-testid={`request-item-${request.id}`}
                  style={{
                    backgroundColor: 'var(--white)',
                    border: '1px solid var(--soft-gray)',
                    padding: 'var(--space-l)',
                    cursor: 'pointer',
                    transition: 'all 200ms ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--black)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--soft-gray)'}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 'var(--space-m)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-m)',
                        marginBottom: 'var(--space-s)'
                      }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          backgroundColor: 'var(--cream)',
                          fontFamily: 'var(--font-body)',
                          fontSize: '0.75rem'
                        }}>
                          <TypeIcon size={12} />
                          {type.label}
                        </span>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          backgroundColor: `${status.color}15`,
                          color: status.color,
                          fontFamily: 'var(--font-body)',
                          fontSize: '0.75rem'
                        }}>
                          <StatusIcon size={12} />
                          {status.label}
                        </span>
                      </div>

                      <p style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.9375rem',
                        lineHeight: 1.5,
                        marginBottom: 'var(--space-s)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {request.description}
                      </p>

                      <p style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.75rem',
                        color: 'var(--light-gray)'
                      }}>
                        {new Date(request.created_at).toLocaleDateString('ru-RU')}
                      </p>
                    </div>

                    {request.photos && request.photos.length > 0 && (
                      <img
                        src={request.photos[0]}
                        alt=""
                        style={{
                          width: '70px',
                          height: '70px',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientRequests;
